import React, { useState } from 'react';
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, AlignVerticalSpaceAround, Baseline, Bold, Braces, Brush, Columns2, Eraser,
  Heading1, Heading2, Heading3, Highlighter, Image as ImageIcon, IndentDecrease, IndentIncrease, Italic, Link, List, ListOrdered,
  ListTree, Minus, PenTool, Pilcrow, Plus, QrCode, Redo2, Rows3, ScrollText, Search, SeparatorHorizontal, Strikethrough,
  Subscript, Superscript, Table2, Type, Underline, Undo2, Users, ZoomIn, ZoomOut,
} from 'lucide-react';
import { ColorMenu, HighlightMenu, Menu, MenuItem, Separator, ToolButton } from './ui';
import { EditorApi } from './commands';
import { FONT_FAMILIES, FONT_SIZES, LINE_HEIGHTS, uid } from './model';
import SignatureBlockDialog from './signatureBlock';

const ALIGNMENTS = [
  { value: 'left' as const, label: 'Linksbündig', icon: <AlignLeft />, shortcut: 'Strg+Umschalt+L' },
  { value: 'center' as const, label: 'Zentriert', icon: <AlignCenter />, shortcut: 'Strg+Umschalt+E' },
  { value: 'right' as const, label: 'Rechtsbündig', icon: <AlignRight />, shortcut: 'Strg+Umschalt+R' },
  { value: 'justify' as const, label: 'Blocksatz', icon: <AlignJustify />, shortcut: 'Strg+Umschalt+J' },
];

/** Ribbon-artige Werkzeugleiste mit Schrift-, Absatz-, Einfüge- und Ansichtsgruppen. */
export default function Toolbar({ api }: { api: EditorApi }) {
  const { flags, style } = api;
  const [signatureDialog, setSignatureDialog] = useState(false);
  const fontSize = flags.fontSize ?? style.fontSize ?? 10;
  const namedStyle = api.document.styles?.find(entry => entry.id === api.node?.style?.styleRef);
  const headingLabel = api.node?.type === 'heading' ? `Überschrift ${api.node.level || 1}` : api.node?.type === 'list' ? 'Liste' : 'Standardtext';

  const stepFontSize = (delta: number) => {
    const next = Math.min(200, Math.max(4, Math.round((fontSize + delta) * 10) / 10));
    api.hasSelection ? api.setMark('fontSize', next) : api.setStyle({ fontSize: next });
  };

  return (
    <>
    {signatureDialog && (
      <SignatureBlockDialog
        onClose={() => setSignatureDialog(false)}
        onInsert={node => { api.insert(node); setSignatureDialog(false); }}
      />
    )}
    <div className="sticky top-0 z-30 bg-white border border-gray-200 rounded-lg shadow-sm" role="toolbar" aria-label="Formatierung">
      <div className="flex flex-wrap gap-0.5 items-center p-1.5 border-b border-gray-100">
        <ToolButton label="Rückgängig" shortcut="Strg+Z" disabled={!api.canUndo} onClick={api.undo}><Undo2 /></ToolButton>
        <ToolButton label="Wiederholen" shortcut="Strg+Y" disabled={!api.canRedo} onClick={api.redo}><Redo2 /></ToolButton>
        <ToolButton label="Format übertragen" shortcut="Strg+Umschalt+C" active={api.painter} onClick={api.painter ? api.pasteFormat : api.copyFormat}><Brush /></ToolButton>
        <ToolButton label="Formatierung entfernen" shortcut="Strg+Leertaste" onClick={api.clearFormatting}><Eraser /></ToolButton>
        <Separator />

        <Menu ariaLabel="Formatvorlage" label={namedStyle?.name || headingLabel} width="w-64">
          {close => (
            <>
              <MenuItem active={api.node?.type === 'paragraph' && !api.node?.style?.styleRef} onClick={() => { api.convert('paragraph'); api.applyStyleRef(undefined); close(); }}>Standardtext</MenuItem>
              {([1, 2, 3, 4, 5, 6] as const).map(level => (
                <MenuItem key={level} active={api.node?.type === 'heading' && api.node.level === level} onClick={() => { api.convert('heading', level); close(); }} description={`Strg+Alt+${level}`}>
                  Überschrift {level}
                </MenuItem>
              ))}
              {!!api.document.styles?.length && <div className="my-1 border-t border-gray-100" />}
              {api.document.styles?.map(entry => (
                <MenuItem key={entry.id} active={entry.id === namedStyle?.id} onClick={() => { api.applyStyleRef(entry.id); close(); }}>{entry.name}</MenuItem>
              ))}
              <div className="my-1 border-t border-gray-100" />
              <MenuItem
                onClick={() => {
                  const name = window.prompt('Name der neuen Formatvorlage', `Formatvorlage ${(api.document.styles?.length || 0) + 1}`);
                  if (name?.trim()) api.createStyleFromNode(name.trim());
                  close();
                }}
              >
                Formatvorlage aus Auswahl erstellen …
              </MenuItem>
              {namedStyle && (
                <MenuItem onClick={() => { api.updateStyleFromNode(namedStyle.id); close(); }}>„{namedStyle.name}“ an Auswahl anpassen</MenuItem>
              )}
            </>
          )}
        </Menu>

        <Menu ariaLabel="Schriftart" label={flags.fontFamily || style.fontFamily || 'Helvetica'} width="w-52">
          {close => (
            <>
              {FONT_FAMILIES.map(font => (
                <MenuItem key={font.value} active={(flags.fontFamily || style.fontFamily) === font.value} onClick={() => { api.hasSelection ? api.setMark('fontFamily', font.value) : api.setStyle({ fontFamily: font.value }); close(); }}>
                  <span style={{ fontFamily: font.value.startsWith('Times') ? 'Georgia, serif' : font.value === 'Courier' ? 'monospace' : 'system-ui, sans-serif' }}>{font.label}</span>
                </MenuItem>
              ))}
            </>
          )}
        </Menu>

        <div className="flex items-center">
          <ToolButton label="Schrift verkleinern" onClick={() => stepFontSize(-1)}><Minus /></ToolButton>
          <Menu ariaLabel="Schriftgröße" label={`${fontSize}`} width="w-24">
            {close => (
              <>
                {FONT_SIZES.map(size => (
                  <MenuItem key={size} active={fontSize === size} onClick={() => { api.hasSelection ? api.setMark('fontSize', size) : api.setStyle({ fontSize: size }); close(); }}>{size}</MenuItem>
                ))}
              </>
            )}
          </Menu>
          <ToolButton label="Schrift vergrößern" onClick={() => stepFontSize(1)}><Plus /></ToolButton>
        </div>
        <Separator />

        <ToolButton label="Fett" shortcut="Strg+B" active={!!flags.bold || (!api.hasSelection && !!api.node?.style?.bold)} onClick={() => api.toggleMark('bold')}><Bold /></ToolButton>
        <ToolButton label="Kursiv" shortcut="Strg+I" active={!!flags.italic || (!api.hasSelection && !!api.node?.style?.italic)} onClick={() => api.toggleMark('italic')}><Italic /></ToolButton>
        <ToolButton label="Unterstrichen" shortcut="Strg+U" active={!!flags.underline || (!api.hasSelection && !!api.node?.style?.underline)} onClick={() => api.toggleMark('underline')}><Underline /></ToolButton>
        <ToolButton label="Durchgestrichen" shortcut="Strg+Umschalt+S" active={!!flags.strike || (!api.hasSelection && !!api.node?.style?.strike)} onClick={() => api.toggleMark('strike')}><Strikethrough /></ToolButton>
        <ToolButton label="Hochgestellt" active={!!flags.superscript} onClick={() => api.toggleMark('superscript')}><Superscript /></ToolButton>
        <ToolButton label="Tiefgestellt" active={!!flags.subscript} onClick={() => api.toggleMark('subscript')}><Subscript /></ToolButton>
        <ToolButton label="Kapitälchen" active={!!flags.smallCaps} onClick={() => api.toggleMark('smallCaps')}><Pilcrow /></ToolButton>
        <ColorMenu ariaLabel="Textfarbe" icon={<Baseline />} value={flags.color || style.color} onPick={color => (api.hasSelection ? api.setMark('color', color) : api.setStyle({ color }))} />
        <HighlightMenu ariaLabel="Texthervorhebung" icon={<Highlighter />} value={flags.background || style.background} onPick={color => (api.hasSelection ? api.setMark('background', color) : api.setStyle({ background: color }))} />
        <ToolButton label="Link einfügen" shortcut="Strg+K" active={!!flags.link} onClick={() => api.insertLink()}><Link /></ToolButton>
      </div>

      <div className="flex flex-wrap gap-0.5 items-center p-1.5">
        {ALIGNMENTS.map(item => (
          <ToolButton key={item.value} label={item.label} shortcut={item.shortcut} active={style.align === item.value} onClick={() => api.setStyle({ align: item.value })}>{item.icon}</ToolButton>
        ))}
        <Menu ariaLabel="Zeilenabstand" icon={<Rows3 />} width="w-56">
          {close => (
            <>
              {LINE_HEIGHTS.map(height => (
                <MenuItem key={height} active={style.lineHeight === height} onClick={() => { api.setStyle({ lineHeight: height }); close(); }}>{height.toFixed(2).replace('.', ',')}</MenuItem>
              ))}
              <div className="my-1 border-t border-gray-100" />
              <MenuItem onClick={() => { api.setStyle({ marginTop: (style.marginTop || 0) + 4 }); close(); }}>Abstand davor vergrößern</MenuItem>
              <MenuItem onClick={() => { api.setStyle({ marginTop: Math.max(0, (style.marginTop || 0) - 4) }); close(); }}>Abstand davor verkleinern</MenuItem>
              <MenuItem onClick={() => { api.setStyle({ marginBottom: (style.marginBottom || 0) + 4 }); close(); }}>Abstand danach vergrößern</MenuItem>
              <MenuItem onClick={() => { api.setStyle({ marginBottom: Math.max(0, (style.marginBottom || 0) - 4) }); close(); }}>Abstand danach verkleinern</MenuItem>
            </>
          )}
        </Menu>
        <Separator />
        <ToolButton label="Aufzählung" shortcut="Strg+Umschalt+8" active={api.node?.type === 'list' && !api.node.ordered} onClick={() => api.toggleList(false)}><List /></ToolButton>
        <ToolButton label="Nummerierte Liste" shortcut="Strg+Umschalt+7" active={api.node?.type === 'list' && !!api.node.ordered} onClick={() => api.toggleList(true)}><ListOrdered /></ToolButton>
        <ToolButton label="Einzug verkleinern" shortcut="Umschalt+Tab" onClick={() => api.indent(true)}><IndentDecrease /></ToolButton>
        <ToolButton label="Einzug vergrößern" shortcut="Tab" onClick={() => api.indent(false)}><IndentIncrease /></ToolButton>
        <Separator />

        <Menu ariaLabel="Einfügen" label="Einfügen" icon={<Plus />} width="w-64">
          {close => {
            const run = (node: any) => { api.insert(node); close(); };
            return (
              <>
                <MenuItem onClick={() => run({ id: uid(), type: 'paragraph', text: '' })}>Absatz</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'heading', level: 2, text: 'Überschrift' })}>Überschrift</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'list', ordered: false, items: [{ id: uid(), text: 'Listenpunkt', level: 0 }] })}>Aufzählung</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'list', ordered: true, start: 1, items: [{ id: uid(), text: 'Erster Punkt', level: 0 }] })}>Nummerierte Liste</MenuItem>
                <div className="my-1 border-t border-gray-100" />
                <MenuItem onClick={() => run(newTable())} description="Mit Formeln wie =SUM(B1:B3)">Rechentabelle</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'container', columns: [1, 2].map(index => ({ id: uid(), width: 50, nodes: [{ id: uid(), type: 'paragraph', text: index === 1 ? 'Linke Spalte' : 'Rechte Spalte' }] })) })}>Spaltenbereich</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'image', src: '', alt: '', width: 240 })}>Bild</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'qr', payload: { type: 'URL', url: 'https://' }, size: 90 })}>QR-Code</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'signature', label: 'Unterschrift', provider: 'LOCAL', showDate: true, showPlace: true })}>Signaturfeld</MenuItem>
                <MenuItem onClick={() => { setSignatureDialog(true); close(); }} description="Unterschriftslinien für mehrere Vertragsparteien">Unterschriftenblock …</MenuItem>
                <div className="my-1 border-t border-gray-100" />
                <MenuItem onClick={() => run({ id: uid(), type: 'toc', title: 'Inhaltsverzeichnis', maxLevel: 3 })}>Inhaltsverzeichnis</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'divider', style: { borders: { top: { width: 1, color: '#d1d5db', style: 'solid' } }, marginTop: 8, marginBottom: 8 } })}>Trennlinie</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'spacer', height: 24 })}>Abstandhalter</MenuItem>
                <MenuItem onClick={() => run({ id: uid(), type: 'pageBreak' })} description="Strg+Enter">Seitenumbruch</MenuItem>
              </>
            );
          }}
        </Menu>

        <Menu ariaLabel="Platzhalter einfügen" icon={<Braces />} label="Platzhalter" width="w-64">
          {close => (
            <>
              <MenuItem onClick={() => { const name = window.prompt('Name des Platzhalters (z. B. mieter_name)'); if (name?.trim()) api.insertVariable(name.trim()); close(); }}>Neuer Platzhalter …</MenuItem>
              {api.variables.length > 0 && <div className="my-1 border-t border-gray-100" />}
              {api.variables.map(name => <MenuItem key={name} onClick={() => { api.insertVariable(name); close(); }}>{`<<${name}>>`}</MenuItem>)}
            </>
          )}
        </Menu>

        <ToolButton label="Suchen und Ersetzen" shortcut="Strg+F" onClick={api.toggleFind}><Search /></ToolButton>
        <Separator />
        <ToolButton label="Verkleinern" onClick={() => api.setZoom(Math.max(0.5, Math.round((api.zoom - 0.1) * 10) / 10))}><ZoomOut /></ToolButton>
        <span className="text-xs text-gray-600 w-12 text-center tabular-nums" aria-label="Zoomstufe">{Math.round(api.zoom * 100)} %</span>
        <ToolButton label="Vergrößern" onClick={() => api.setZoom(Math.min(2, Math.round((api.zoom + 0.1) * 10) / 10))}><ZoomIn /></ToolButton>
      </div>
    </div>
    </>
  );
}

export const newTable = () => ({
  id: uid(),
  type: 'table' as const,
  headerRow: true,
  zebra: true,
  borderColor: '#9ca3af',
  columns: [
    { id: uid(), key: 'A', label: 'Position', valueType: 'TEXT', width: 60, align: 'left' },
    { id: uid(), key: 'B', label: 'Betrag', valueType: 'CURRENCY', width: 40, align: 'right', decimals: 2 },
  ],
  rows: [
    { id: uid(), cells: { A: 'Leistung', B: '0.00' } },
    { id: uid(), cells: { A: 'Summe', B: '=SUM(B1:B1)' } },
  ],
});

/** Werkzeugpalette am linken Rand – bewusst als eigene, benannte Schaltflächen. */
export function ElementPalette({ api, children }: { api: EditorApi; children?: React.ReactNode }) {
  const [signatureDialog, setSignatureDialog] = useState(false);
  /** Elemente ohne `make` öffnen zuerst einen Dialog. */
  const tools: { label: string; icon: React.ReactNode; make?: () => any }[] = [
    { label: 'Text', icon: <Type />, make: () => ({ id: uid(), type: 'paragraph', text: 'Neuer Absatz' }) },
    { label: 'Überschrift', icon: <Heading1 />, make: () => ({ id: uid(), type: 'heading', level: 1, text: 'Überschrift' }) },
    { label: 'Unterüberschrift', icon: <Heading2 />, make: () => ({ id: uid(), type: 'heading', level: 2, text: 'Unterüberschrift' }) },
    { label: 'Abschnittstitel', icon: <Heading3 />, make: () => ({ id: uid(), type: 'heading', level: 3, text: 'Abschnitt' }) },
    { label: 'Aufzählung', icon: <List />, make: () => ({ id: uid(), type: 'list', ordered: false, items: [{ id: uid(), text: 'Listenpunkt', level: 0 }] }) },
    { label: 'Nummerierte Liste', icon: <ListOrdered />, make: () => ({ id: uid(), type: 'list', ordered: true, start: 1, items: [{ id: uid(), text: 'Erster Punkt', level: 0 }] }) },
    { label: 'Spaltenbereich', icon: <Columns2 />, make: () => ({ id: uid(), type: 'container', columns: [1, 2].map(index => ({ id: uid(), width: 50, nodes: [{ id: uid(), type: 'paragraph', text: index === 1 ? 'Linke Spalte' : 'Rechte Spalte' }] })) }) },
    { label: 'Rechentabelle', icon: <Table2 />, make: newTable },
    { label: 'Bild', icon: <ImageIcon />, make: () => ({ id: uid(), type: 'image', src: '', alt: '', width: 240 }) },
    { label: 'QR-Code', icon: <QrCode />, make: () => ({ id: uid(), type: 'qr', payload: { type: 'URL', url: 'https://' }, size: 90 }) },
    { label: 'Signatur', icon: <PenTool />, make: () => ({ id: uid(), type: 'signature', label: 'Unterschrift', provider: 'LOCAL', showDate: true, showPlace: true }) },
    { label: 'Unterschriftenblock', icon: <Users /> },
    { label: 'Inhaltsverzeichnis', icon: <ListTree />, make: () => ({ id: uid(), type: 'toc', title: 'Inhaltsverzeichnis', maxLevel: 3 }) },
    { label: 'Trennlinie', icon: <SeparatorHorizontal />, make: () => ({ id: uid(), type: 'divider', style: { borders: { top: { width: 1, color: '#d1d5db', style: 'solid' } }, marginTop: 8, marginBottom: 8 } }) },
    { label: 'Abstandhalter', icon: <AlignVerticalSpaceAround />, make: () => ({ id: uid(), type: 'spacer', height: 24 }) },
    { label: 'Seitenumbruch', icon: <ScrollText />, make: () => ({ id: uid(), type: 'pageBreak' }) },
  ];
  return (
    <aside className="bg-white border border-gray-200 rounded-lg p-3 h-fit" aria-label="Elementpalette">
      {signatureDialog && (
        <SignatureBlockDialog
          onClose={() => setSignatureDialog(false)}
          onInsert={node => { api.insert(node); setSignatureDialog(false); }}
        />
      )}
      <h3 className="font-semibold text-sm mb-3">Elemente</h3>
      <div className="space-y-1.5">
        {tools.map(tool => (
          <button
            key={tool.label}
            type="button"
            onClick={() => (tool.make ? api.insert(tool.make()) : setSignatureDialog(true))}
            className="w-full flex gap-2 items-center border border-gray-200 rounded p-2 text-sm hover:bg-primary-50 hover:border-primary-200"
          >
            {React.cloneElement(tool.icon as any, { size: 15 })}
            {tool.label}
          </button>
        ))}
      </div>
      {children}
    </aside>
  );
}
